// Dwoft Shopify Theme Scripts
function initDwoftTheme() {
  setupCartDrawer();
  setupCartDrawerActions();
  setupSearchModal();
  setupWishlistModal();
  setupUniversalMenuDrawer();
  setupStickyHeader();
  setupSizeGuideModal();
  setupProductForms();
  setupCountdowns();
  setupCustomCursorAndSpotlight();
  setupContactScroll();
  setupHeroSlider();
  setupQuickAdd();
  setupQuickAddDrawer();
  setupMobileNavActiveState();
  refreshCartBadges();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDwoftTheme);
} else {
  initDwoftTheme();
}

// Slide-out Ajax Cart Drawer
function setupCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  if (!drawer || !overlay) return;

  function openCart() {
    if (window.location.pathname.includes('/cart')) return;
    drawer.classList.remove('translate-x-full');
    overlay.classList.remove('opacity-0', 'pointer-events-none');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    drawer.classList.add('translate-x-full');
    overlay.classList.add('opacity-0', 'pointer-events-none');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-action="open-cart"]');
    if (openBtn) {
      e.preventDefault();
      openCart();
      return;
    }
    const closeBtn = e.target.closest('[data-action="close-cart"]');
    if (closeBtn) {
      e.preventDefault();
      closeCart();
      return;
    }
    if (e.target === overlay) {
      e.preventDefault();
      closeCart();
      return;
    }
  });

  window.openDwoftCart = openCart;
  window.closeDwoftCart = closeCart;
}

// Customer Authentication Helper & Login Redirect
function isCustomerLoggedIn() {
  return Boolean(window.DWOFT_CUSTOMER && window.DWOFT_CUSTOMER.isLoggedIn);
}

function redirectToLogin(targetUrl) {
  const returnTo = targetUrl || (window.location.pathname + window.location.search);
  const loginUrl = (window.DWOFT_CUSTOMER && window.DWOFT_CUSTOMER.loginUrl) ? window.DWOFT_CUSTOMER.loginUrl : '/account/login';
  try {
    sessionStorage.setItem('dwoft_return_to', returnTo);
  } catch (err) {}
  window.location.href = `${loginUrl}?return_to=${encodeURIComponent(returnTo)}`;
}

// Synchronize "ADDED!" banners on product cards with current cart contents
function updateCartAddedBanners(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  const inCartProdIds = new Set(cart.items.map(i => String(i.product_id)));
  const inCartVarIds = new Set(cart.items.map(i => String(i.id)));

  document.querySelectorAll('.cart-added-banner').forEach(banner => {
    const prodId = banner.getAttribute('data-cart-banner-product-id');
    const varIdsStr = banner.getAttribute('data-cart-banner-variant-ids') || '';
    const varIds = varIdsStr ? varIdsStr.split(',').map(s => s.trim()) : [];

    const isInCart = (prodId && inCartProdIds.has(String(prodId))) || varIds.some(vid => inCartVarIds.has(String(vid)));

    if (isInCart) {
      banner.classList.remove('hidden');
      requestAnimationFrame(() => {
        banner.classList.remove('opacity-0', 'scale-75', '-translate-y-1');
        banner.classList.add('inline-flex', 'opacity-100', 'scale-100', 'translate-y-0');
      });
    } else {
      banner.classList.remove('opacity-100', 'scale-100', 'translate-y-0');
      banner.classList.add('opacity-0', 'scale-75', '-translate-y-1');
      setTimeout(() => {
        if (!banner.classList.contains('opacity-100')) {
          banner.classList.add('hidden');
          banner.classList.remove('inline-flex');
        }
      }, 300);
    }
  });
}
window.updateCartAddedBanners = updateCartAddedBanners;

// Synchronize cart counter badges across header, mobile bottom nav, and drawers
async function refreshCartBadges() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return null;
    const cart = await res.json();
    document.querySelectorAll('.cart-count-badge').forEach(badge => {
      badge.textContent = cart.item_count;
    });
    if (typeof renderCartUI === 'function') {
      renderCartUI(cart);
    }
    updateCartAddedBanners(cart);
    return cart;
  } catch (err) {
    console.error('Failed to sync cart badges:', err);
    return null;
  }
}
window.refreshCartBadges = refreshCartBadges;

// Ajax add-to-cart listener for standard Shopify forms
function setupProductForms() {
  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      // If the submitter is the Buy Now button, let Shopify process standard direct checkout
      const submitter = e.submitter;
      if (submitter && submitter.getAttribute('name') === 'checkout') {
        return; // Allow standard form submission to checkout
      }

      e.preventDefault();
      const addBtn = form.querySelector('#pdp-add-to-bag, [type="submit"]');
      const origBtnText = addBtn ? addBtn.innerHTML : '';
      const isQuickAdd = addBtn && (addBtn.getAttribute('data-action') === 'ajax-quick-add' || form.closest('.card-brutal-hover') || (submitter && submitter.getAttribute('data-action') === 'ajax-quick-add'));

      if (addBtn) {
        addBtn.disabled = true;
        if (isQuickAdd) {
          addBtn.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> <span>ADDING...</span>';
        } else {
          addBtn.innerHTML = '<svg class="w-4 h-4 animate-spin inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> <span>SECURING FIT...</span>';
        }
      }

      const formData = new FormData(form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        if (!response.ok) throw new Error('Add to cart failed');

        if (isQuickAdd) {
          // Update cart counter badges & banners immediately
          await refreshCartBadges();

          // Immediately display ADDED! banner on this card
          const card = addBtn ? (addBtn.closest('.card-brutal-hover') || form.closest('.card-brutal-hover')) : null;
          if (card) {
            const banner = card.querySelector('.cart-added-banner');
            if (banner) {
              banner.classList.remove('hidden');
              requestAnimationFrame(() => {
                banner.classList.remove('opacity-0', 'scale-90');
                banner.classList.add('inline-flex', 'opacity-100', 'scale-100');
              });
            }
          }

          if (addBtn) {
            addBtn.classList.remove('bg-dwoft-red', 'hover:bg-[#E00028]');
            addBtn.classList.add('bg-emerald-600', 'hover:bg-emerald-500');
            addBtn.innerHTML = '<svg class="w-3.5 h-3.5 inline-block mr-1 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> <span>ADDED</span>';
            setTimeout(() => {
              addBtn.classList.remove('bg-emerald-600', 'hover:bg-emerald-500');
              addBtn.classList.add('bg-dwoft-red', 'hover:bg-[#E00028]');
              addBtn.innerHTML = origBtnText;
              addBtn.disabled = false;
            }, 2000);
          }
        } else {
          // Dedicated PDP Add to Bag: refresh badges, show success, and open cart drawer
          await refreshCartBadges();

          if (addBtn) {
            addBtn.classList.remove('bg-[#FF6A00]');
            addBtn.classList.add('bg-emerald-600');
            addBtn.innerHTML = '<svg class="w-4 h-4 inline-block mr-1 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> <span>ADDED TO BAG!</span>';
            setTimeout(() => {
              addBtn.classList.remove('bg-emerald-600');
              addBtn.innerHTML = origBtnText;
              addBtn.disabled = false;
            }, 2500);
          }

          if (typeof window.openDwoftCart === 'function') {
            window.openDwoftCart();
          } else {
            const drawer = document.getElementById('cart-drawer');
            const overlay = document.getElementById('cart-overlay');
            if (drawer) drawer.classList.remove('translate-x-full');
            if (overlay) overlay.classList.remove('opacity-0', 'pointer-events-none');
          }
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
        form.submit(); // fallback to standard Shopify submission
      } finally {
        if (addBtn && !isQuickAdd) {
          addBtn.disabled = false;
          addBtn.innerHTML = origBtnText;
        }
      }
    });
  });

  // PDP Quantity Stepper
  const qtyInput = document.getElementById('pdp-quantity-input');
  const qtyDec = document.getElementById('pdp-qty-dec');
  const qtyInc = document.getElementById('pdp-qty-inc');
  if (qtyDec && qtyInput) {
    qtyDec.addEventListener('click', (e) => {
      e.preventDefault();
      let val = parseInt(qtyInput.value || '1', 10);
      if (val > 1) qtyInput.value = val - 1;
    });
  }
  if (qtyInc && qtyInput) {
    qtyInc.addEventListener('click', (e) => {
      e.preventDefault();
      let val = parseInt(qtyInput.value || '1', 10);
      if (val < 10) qtyInput.value = val + 1;
    });
  }
}

// Size Guide Modal
function setupSizeGuideModal() {
  const modal = document.getElementById('size-guide-modal');
  if (!modal) return;

  function openSizeGuide() {
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeSizeGuide() {
    modal.classList.add('hidden');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="open-size-guide"]')) {
      e.preventDefault();
      openSizeGuide();
      return;
    }
    if (e.target.closest('#close-size-guide') || e.target === modal) {
      e.preventDefault();
      closeSizeGuide();
      return;
    }
  });

  window.openDwoftSizeGuide = openSizeGuide;
  window.closeDwoftSizeGuide = closeSizeGuide;
}

// Dynamic Countdown Timers (Admin & Metafield driven)
function setupCountdowns() {
  const timerElements = document.querySelectorAll('[data-drop-end]');
  if (!timerElements.length) return;

  function updateTimers() {
    const now = Date.now();

    timerElements.forEach(el => {
      const endAttr = el.getAttribute('data-drop-end');
      if (!endAttr) return;

      const endTime = new Date(endAttr).getTime();
      const diff = endTime - now;

      if (isNaN(diff) || diff <= 0) {
        const expiredText = el.getAttribute('data-drop-expired') || 'DROP CLOSED';
        el.innerHTML = `<span class="text-dwoft-pink font-bold uppercase tracking-wider text-xs">${expiredText}</span>`;
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const pad = (n) => String(n).padStart(2, '0');

      if (days > 0) {
        el.innerHTML = `
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${days}d</span>
          <span>:</span>
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(hours)}h</span>
          <span>:</span>
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(minutes)}m</span>
          <span>:</span>
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(seconds)}s</span>
        `;
      } else {
        el.innerHTML = `
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(hours)}h</span>
          <span>:</span>
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(minutes)}m</span>
          <span>:</span>
          <span class="bg-zinc-100 text-dwoft-dark px-1.5 py-0.5 rounded border border-dwoft-dark">${pad(seconds)}s</span>
        `;
      }
    });
  }

  updateTimers();
  setInterval(updateTimers, 1000);
}

// Custom Cursor disabled per client request - Enforce native OS cursor
function setupCustomCursorAndSpotlight() {
  document.documentElement.classList.remove('has-custom-cursor');
  document.body.classList.remove('has-custom-cursor', 'cursor-hovering');
  document.querySelectorAll('.custom-cursor-dot, .custom-cursor-ring').forEach(el => el.remove());
}

// Universal Menu Drawer (Desktop & Mobile)
function setupUniversalMenuDrawer() {
  const drawer = document.getElementById('menu-drawer');
  const overlay = document.getElementById('menu-drawer-overlay');

  if (!drawer) return;

  const openDrawer = () => {
    drawer.classList.remove('-translate-x-full');
    drawer.style.transform = 'translateX(0)';
    if (overlay) {
      overlay.classList.remove('opacity-0', 'pointer-events-none');
      overlay.classList.add('opacity-100');
      overlay.style.opacity = '1';
      overlay.style.pointerEvents = 'auto';
    }
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    drawer.classList.add('-translate-x-full');
    drawer.style.transform = 'translateX(-100%)';
    if (overlay) {
      overlay.classList.add('opacity-0', 'pointer-events-none');
      overlay.classList.remove('opacity-100');
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
    }
    document.body.style.overflow = '';
  };

  window.openDwoftMenu = openDrawer;
  window.closeDwoftMenu = closeDrawer;

  document.addEventListener('click', (e) => {
    if (e.target.closest('#menu-drawer-toggle, #mobile-menu-toggle, [data-action="open-menu"]')) {
      e.preventDefault();
      openDrawer();
    } else if (e.target.closest('#close-menu-drawer, [data-action="close-menu"]')) {
      e.preventDefault();
      closeDrawer();
    } else if (e.target === overlay) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
    }
  });
}

// Scroll-Morph Header Functionality (Desktop & Mobile Always On Display on Scroll)
function setupStickyHeader() {
  const header = document.getElementById('main-header') || document.querySelector('header');
  if (!header) return;

  const sectionWrapper = header.closest('#shopify-section-header, .shopify-section-header, div[id*="shopify-section-header"], .shopify-section') || header.parentElement;

  const handleScroll = () => {
    const announcementBar = document.querySelector('.announcement-bar') || document.getElementById('shopify-section-announcement-bar');
    const threshold = (announcementBar && announcementBar.offsetHeight) ? announcementBar.offsetHeight : 0;

    if (window.scrollY > threshold) {
      if (sectionWrapper && !sectionWrapper.style.minHeight) {
        sectionWrapper.style.minHeight = `${header.offsetHeight}px`;
      }
      header.classList.add('header-scrolled');
    } else {
      header.classList.remove('header-scrolled');
      if (sectionWrapper) {
        sectionWrapper.style.minHeight = '';
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll, { passive: true });
  handleScroll();

  // Tap on crest logo to scroll back to top of page
  const crestTriggers = document.querySelectorAll('#header-crest-logo, .crest-logo-trigger');
  crestTriggers.forEach(crestTrigger => {
    crestTrigger.addEventListener('click', (e) => {
      // If user is scrolled down, scroll smoothly back to the top
      if (window.scrollY > 20) {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        // If already at top and on a subpage, navigate to homepage
        const isHome = window.location.pathname === '/' || window.location.pathname === '';
        if (!isHome) {
          window.location.href = '/';
        } else {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    });
  });
}

// Setup drawer "CONTACT" link to smoothly glide to footer
function setupContactScroll() {
  const contactTriggers = document.querySelectorAll('[data-action="scroll-to-footer"]');
  contactTriggers.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      // Close menu drawer if open
      const closeBtn = document.getElementById('close-menu-drawer');
      if (closeBtn) closeBtn.click();

      setTimeout(() => {
        const footer = document.getElementById('footer') || document.querySelector('footer');
        if (footer) {
          footer.scrollIntoView({ behavior: 'smooth' });
        }
      }, 250);
    });
  });
}




// Synchronize Mobile Bottom Dock Active Navigation State with Vivid Orange Highlight
function setupMobileNavActiveState() {
  const dock = document.getElementById('dwoft-mobile-dock');
  if (!dock) return;

  const currentPath = window.location.pathname.toLowerCase();
  const searchStr = window.location.search.toLowerCase();

  let activeTarget = 'home';
  if (currentPath.includes('/cart')) {
    activeTarget = 'bag';
  } else if (currentPath.includes('wishlist') || searchStr.includes('wishlist')) {
    activeTarget = 'saved';
  } else if (currentPath.includes('/account')) {
    activeTarget = 'account';
  } else if (currentPath.includes('/products') || currentPath.includes('/collections')) {
    activeTarget = 'products';
  } else if (currentPath === '/' || currentPath === '' || currentPath.endsWith('/index')) {
    activeTarget = 'home';
  }

  dock.querySelectorAll('a[data-nav-target]').forEach(link => {
    const target = link.getAttribute('data-nav-target');
    const indicator = link.querySelector('.active-indicator');
    if (target === activeTarget) {
      link.classList.add('is-active', 'text-[#FF6A00]');
      link.classList.remove('text-[#A1A1AA]');
      link.style.setProperty('color', '#FF6A00', 'important');
      if (indicator) {
        indicator.classList.remove('hidden');
        indicator.style.setProperty('display', 'block', 'important');
      }
    } else {
      link.classList.remove('is-active', 'text-[#FF6A00]');
      link.classList.add('text-[#A1A1AA]');
      link.style.setProperty('color', '#A1A1AA', 'important');
      if (indicator) {
        indicator.classList.add('hidden');
        indicator.style.setProperty('display', 'none', 'important');
      }
    }
  });
}
window.setupMobileNavActiveState = setupMobileNavActiveState;

// Hero Drop Multi-Slide Reel Navigation & Gestures
function setupHeroSlider() {
  const container = document.getElementById('hero-slides-container');
  if (!container) return;

  const slides = container.querySelectorAll('.hero-slide');
  const pills = container.querySelectorAll('[data-hero-slide-to]');
  if (slides.length <= 1) return;

  // Prevent duplicate binding
  if (container.dataset.sliderInitialized === 'true') {
    return;
  }
  container.dataset.sliderInitialized = 'true';

  let currentIdx = 0;
  let autoplayTimer = null;
  const isAutoplay = container.getAttribute('data-autoplay') === 'true';
  const autoplayInterval = parseInt(container.getAttribute('data-autoplay-interval') || '5', 10) * 1000;

  function goToSlide(targetIdx) {
    currentIdx = (targetIdx + slides.length) % slides.length;

    slides.forEach((slide, i) => {
      if (i === currentIdx) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    pills.forEach((pill, i) => {
      if (i === currentIdx) {
        pill.classList.add('active', 'w-10', 'bg-[#FF6A00]');
        pill.classList.remove('w-6', 'bg-[#E8E3D9]', 'border-[#D4CEB8]');
        pill.style.boxShadow = '0 2px 8px rgba(255,106,0,0.35)';
      } else {
        pill.classList.remove('active', 'w-10', 'bg-[#FF6A00]');
        pill.classList.add('w-6', 'bg-[#E8E3D9]', 'border-[#D4CEB8]');
        pill.style.boxShadow = 'none';
      }
    });

    resetAutoplay();
  }

  function nextSlide() {
    goToSlide(currentIdx + 1);
  }

  function prevSlide() {
    goToSlide(currentIdx - 1);
  }

  function resetAutoplay() {
    if (!isAutoplay) return;
    if (autoplayTimer) clearInterval(autoplayTimer);
    autoplayTimer = setInterval(nextSlide, autoplayInterval);
  }

  // Bullet pills manual navigation
  pills.forEach(pill => {
    pill.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const targetIdx = parseInt(pill.getAttribute('data-hero-slide-to'), 10);
      goToSlide(targetIdx);
    });
  });

  // Floating Side Prev / Next button controls (reliable container delegation)
  container.addEventListener('click', (e) => {
    const prevTrigger = e.target.closest('[data-action="hero-prev"]');
    const nextTrigger = e.target.closest('[data-action="hero-next"]');

    if (prevTrigger) {
      e.preventDefault();
      e.stopPropagation();
      prevSlide();
      return;
    }
    if (nextTrigger) {
      e.preventDefault();
      e.stopPropagation();
      nextSlide();
      return;
    }
  });

  // Mobile swipe support without layout shift or horizontal rubber-banding
  let touchStartX = 0;
  let touchStartY = 0;
  let touchEndX = 0;
  let touchEndY = 0;
  let isSwiping = false;

  container.addEventListener('touchstart', (e) => {
    if (e.touches.length > 1) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    isSwiping = true;
  }, { passive: true });

  container.addEventListener('touchmove', (e) => {
    if (!isSwiping || e.touches.length > 1) return;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX;
    const diffY = currentY - touchStartY;

    // When horizontal swipe is intentional, lock native page panning
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      if (e.cancelable) e.preventDefault();
    }
  }, { passive: false });

  container.addEventListener('touchend', (e) => {
    if (!isSwiping) return;
    isSwiping = false;
    touchEndX = e.changedTouches[0].clientX;
    touchEndY = e.changedTouches[0].clientY;

    const diffX = touchEndX - touchStartX;
    const diffY = touchEndY - touchStartY;

    if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY) * 1.3) {
      if (diffX < 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
  }, { passive: true });

  resetAutoplay();

  // Expose to window for reinitialization on Shopify section reloads
  window.setupHeroSlider = setupHeroSlider;
}

// Standalone quick-add to bag for hero and cards
function setupQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-action="quick-add"]');
    if (!btn) return;

    e.preventDefault();

    const variantId = btn.getAttribute('data-variant-id');
    const productUrl = btn.getAttribute('data-product-url');

    if (!variantId) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    const origContent = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> <span class="text-[10px] tracking-wider uppercase font-black">ADDING...</span>';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      // Update cart count badges - DO NOT REDIRECT TO /cart
      await refreshCartBadges();

      const card = btn.closest('.card-brutal-hover, .hero-slide');
      if (card) {
        const banner = card.querySelector('.cart-added-banner');
        if (banner) {
          banner.classList.remove('hidden');
          requestAnimationFrame(() => {
            banner.classList.remove('opacity-0', 'scale-90');
            banner.classList.add('inline-flex', 'opacity-100', 'scale-100');
          });
        }
      }

      btn.classList.remove('bg-dwoft-red', 'hover:bg-[#E00028]');
      btn.classList.add('bg-emerald-600', 'hover:bg-emerald-500');
      btn.innerHTML = '<svg class="w-3.5 h-3.5 inline-block mr-1 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> <span class="text-[10px] tracking-wider uppercase font-black">ADDED</span>';

      if (typeof window.openDwoftCart === 'function') {
        window.openDwoftCart();
      }

      setTimeout(() => {
        btn.classList.remove('bg-emerald-600', 'hover:bg-emerald-500');
        btn.classList.add('bg-dwoft-red', 'hover:bg-[#E00028]');
        btn.innerHTML = origContent;
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.warn('Quick add error, redirecting to product:', err);
      if (productUrl) window.location.href = productUrl;
      btn.innerHTML = origContent;
      btn.disabled = false;
    }
  });
}

// Mobile-First Quick Add Drawer: Size & Quantity Selector
function setupQuickAddDrawer() {
  const drawer = document.getElementById('quick-add-drawer');
  const overlay = document.getElementById('quick-add-overlay');
  const closeBtn = document.getElementById('close-quick-add-drawer');
  if (!drawer || !overlay) return;

  let currentVariants = [];
  let selectedVariant = null;
  let currentQty = 1;
  let activeCard = null;

  function formatMoney(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  // Quantity stepper listeners
  const qtyDisplay = document.getElementById('quick-add-qty-display');
  const decBtn = document.getElementById('quick-add-qty-dec');
  const incBtn = document.getElementById('quick-add-qty-inc');
  const totalBtnPrice = document.getElementById('quick-add-total-btn-price');

  function updateTotals() {
    if (qtyDisplay) qtyDisplay.textContent = currentQty;
    if (totalBtnPrice && selectedVariant) {
      const priceCents = selectedVariant.price;
      totalBtnPrice.textContent = formatMoney(priceCents * currentQty);
    }
  }

  if (decBtn) {
    decBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (currentQty > 1) {
        currentQty--;
        updateTotals();
      }
    });
  }

  if (incBtn) {
    incBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (currentQty < 10) {
        currentQty++;
        updateTotals();
      }
    });
  }

  // Delegated click listener to trigger drawer from product cards or buttons
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-action="open-quick-add-drawer"]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    activeCard = trigger.closest('.card-brutal-hover') || trigger.closest('.shop-item-card') || trigger.closest('.hero-slide') || trigger.parentElement;

    const title = trigger.getAttribute('data-product-title') || 'Dwoft Streetwear';
    const price = trigger.getAttribute('data-product-price') || '';
    const compare = trigger.getAttribute('data-product-compare') || '';
    const image = trigger.getAttribute('data-product-image') || '';

    // Elements in drawer
    const imgEl = document.getElementById('quick-add-img');
    const titleEl = document.getElementById('quick-add-title');
    const priceEl = document.getElementById('quick-add-price');
    const compareEl = document.getElementById('quick-add-compare');
    const sizesContainer = document.getElementById('quick-add-sizes-container');
    const selectedSizeLabel = document.getElementById('quick-add-selected-size-label');

    if (imgEl) {
      imgEl.src = image;
      imgEl.alt = title;
    }
    if (titleEl) titleEl.textContent = title;
    if (priceEl) priceEl.textContent = price;
    if (compareEl) {
      compareEl.textContent = compare;
      compareEl.style.display = compare ? 'inline' : 'none';
    }

    // Reset qty
    currentQty = 1;

    // Parse variants and options JSON
    let variants = [];
    let options = [];
    const scriptTag = activeCard ? activeCard.querySelector('.product-variants-json') : (trigger.parentElement ? trigger.parentElement.querySelector('.product-variants-json') : null);
    if (scriptTag) {
      try {
        const parsed = JSON.parse(scriptTag.textContent);
        if (Array.isArray(parsed)) {
          variants = parsed;
        } else if (parsed && parsed.variants) {
          variants = parsed.variants;
          options = parsed.options || [];
        }
      } catch (err) {
        console.warn('Failed to parse product variants json:', err);
      }
    }

    // Fallback fetch if missing
    if (!variants || variants.length === 0) {
      const prodUrl = trigger.getAttribute('data-product-url');
      if (prodUrl) {
        const handle = prodUrl.split('/').pop().split('?')[0];
        if (handle) {
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (res.ok) {
              const pData = await res.json();
              if (pData && pData.variants) {
                variants = pData.variants;
                options = pData.options || [];
              }
            }
          } catch (err) {}
        }
      }
    }

    currentVariants = variants;

    // Detect Color and Size option positions
    let colorOpt = null;
    let sizeOpt = null;

    if (Array.isArray(options) && options.length > 0) {
      colorOpt = options.find(o => {
        const name = (typeof o === 'string' ? o : (o.name || '')).toLowerCase();
        return name.includes('color') || name.includes('colour');
      });
      sizeOpt = options.find(o => {
        const name = (typeof o === 'string' ? o : (o.name || '')).toLowerCase();
        return name.includes('size') || name.includes('fit');
      });
    }

    let colorPos = colorOpt ? (colorOpt.position || 1) : null;
    let sizePos = sizeOpt ? (sizeOpt.position || (colorPos === 1 ? 2 : 1)) : null;

    if (!sizePos && variants.length > 0) {
      const first = variants[0];
      if (first.option2 || (first.options && first.options.length >= 2)) {
        colorPos = 1;
        sizePos = 2;
      } else {
        sizePos = 1;
      }
    }

    function getVariantSize(v) {
      if (sizePos === 2 && v.option2) return v.option2;
      if (sizePos === 3 && v.option3) return v.option3;
      if (sizePos === 1 && v.option1) return v.option1;
      if (v.options && sizePos && v.options[sizePos - 1]) return v.options[sizePos - 1];
      if (v.title && v.title.includes('/')) {
        const parts = v.title.split('/').map(s => s.trim());
        return parts[parts.length - 1];
      }
      if (v.options && v.options.length > 1) return v.options[v.options.length - 1];
      if (v.options && v.options.length === 1) return v.options[0];
      return v.title || 'STANDARD';
    }

    function getVariantColor(v) {
      if (colorPos === 1 && v.option1) return v.option1;
      if (colorPos === 2 && v.option2) return v.option2;
      if (v.options && colorPos && v.options[colorPos - 1]) return v.options[colorPos - 1];
      if (v.title && v.title.includes('/')) {
        return v.title.split('/')[0].trim();
      }
      return v.option1 || null;
    }

    // Determine list of available sizes and colors
    let sizeValues = [];
    if (sizeOpt && Array.isArray(sizeOpt.values) && sizeOpt.values.length > 0) {
      sizeValues = sizeOpt.values;
    } else {
      sizeValues = [...new Set(variants.map(getVariantSize))];
    }

    let colorValues = [];
    if (colorOpt && Array.isArray(colorOpt.values) && colorOpt.values.length > 0) {
      colorValues = colorOpt.values;
    } else {
      colorValues = [...new Set(variants.map(getVariantColor).filter(Boolean))];
    }

    // Set initial selection
    const firstAvail = variants.find(v => v.available) || variants[0];
    selectedVariant = firstAvail;

    let selectedColor = firstAvail ? getVariantColor(firstAvail) : (colorValues[0] || null);
    let selectedSize = firstAvail ? getVariantSize(firstAvail) : (sizeValues[0] || null);

    // Color Selector elements
    const colorSection = document.getElementById('quick-add-color-section');
    const colorsContainer = document.getElementById('quick-add-colors-container');
    const selectedColorLabel = document.getElementById('quick-add-selected-color-label');

    function updateMatchingVariant() {
      const match = variants.find(v => {
        const matchColor = !selectedColor || getVariantColor(v) === selectedColor;
        const matchSize = !selectedSize || getVariantSize(v) === selectedSize;
        return matchColor && matchSize;
      }) || variants.find(v => !selectedSize || getVariantSize(v) === selectedSize) || variants[0];

      if (match) {
        selectedVariant = match;
        if (match.price) {
          if (priceEl) priceEl.textContent = match.price_formatted || formatMoney(match.price);
        }
        if (match.featured_image && imgEl) {
          imgEl.src = match.featured_image;
        }
      }
      updateTotals();
    }

    // Render Colors if > 1
    if (colorSection && colorsContainer) {
      if (colorValues.length > 1) {
        colorSection.classList.remove('hidden');
        if (selectedColorLabel) selectedColorLabel.textContent = selectedColor;

        colorsContainer.innerHTML = colorValues.map(c => `
          <button 
            type="button" 
            data-quick-color="${c}"
            class="quick-add-color-pill ${c === selectedColor ? 'is-selected' : ''}"
          >
            ${c}
          </button>
        `).join('');

        colorsContainer.querySelectorAll('[data-quick-color]').forEach(cBtn => {
          cBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            selectedColor = cBtn.getAttribute('data-quick-color');
            if (selectedColorLabel) selectedColorLabel.textContent = selectedColor;

            colorsContainer.querySelectorAll('.quick-add-color-pill').forEach(b => b.classList.remove('is-selected'));
            cBtn.classList.add('is-selected');

            updateMatchingVariant();
            renderSizes();
          });
        });
      } else {
        colorSection.classList.add('hidden');
      }
    }

    // Render Sizes
    function renderSizes() {
      if (!sizesContainer) return;
      sizesContainer.innerHTML = '';

      if (!sizeValues || sizeValues.length === 0) {
        sizesContainer.innerHTML = '<div class="col-span-4 text-xs text-zinc-400 font-mono py-2">STANDARD FIT AVAILABLE</div>';
        return;
      }

      sizeValues.forEach(size => {
        const variantForSize = variants.find(v => {
          const matchColor = !selectedColor || getVariantColor(v) === selectedColor;
          const matchSize = getVariantSize(v) === size;
          return matchColor && matchSize;
        }) || variants.find(v => getVariantSize(v) === size);

        const isAvailable = variantForSize ? variantForSize.available : false;
        const isSelected = selectedSize === size;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `quick-add-size-pill ${isSelected ? 'is-selected' : ''}`;
        btn.textContent = size;

        if (!isAvailable) {
          btn.disabled = true;
        } else {
          btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            selectedSize = size;
            if (selectedSizeLabel) selectedSizeLabel.textContent = size;

            sizesContainer.querySelectorAll('.quick-add-size-pill').forEach(b => {
              b.classList.remove('is-selected');
            });
            btn.classList.add('is-selected');

            updateMatchingVariant();
          });
        }

        sizesContainer.appendChild(btn);
      });

      if (selectedSizeLabel && selectedSize) {
        selectedSizeLabel.textContent = selectedSize;
      }
    }

    renderSizes();
    updateMatchingVariant();
    openDrawer();
  });

  // Submit button
  const submitBtn = document.getElementById('quick-add-submit-btn');
  if (submitBtn) {
    submitBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (!selectedVariant || !selectedVariant.id) {
        alert('Please select a size first.');
        return;
      }

      const origHtml = submitBtn.innerHTML;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<svg class="w-4 h-4 animate-spin inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> <span>ADDING TO BAG...</span>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(selectedVariant.id), quantity: Number(currentQty) })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        submitBtn.innerHTML = '<svg class="w-4 h-4 inline-block mr-1 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> <span>ADDED TO BAG!</span>';

        // Update cart counter badges & banners immediately
        await refreshCartBadges();

        // Highlight the protruding ADDED! banner on the active card
        if (activeCard) {
          const banner = activeCard.querySelector('.cart-added-banner');
          if (banner) {
            banner.classList.remove('hidden');
            requestAnimationFrame(() => {
              banner.classList.remove('opacity-0', 'scale-75', '-translate-y-1');
              banner.classList.add('inline-flex', 'opacity-100', 'scale-100', 'translate-y-0');
            });
          }
        }

        setTimeout(() => {
          closeDrawer();
          submitBtn.innerHTML = origHtml;
          submitBtn.disabled = false;
          if (typeof window.openDwoftCart === 'function') {
            window.openDwoftCart();
          }
        }, 500);

      } catch (err) {
        console.error('Quick Add submit error:', err);
        submitBtn.innerHTML = origHtml;
        submitBtn.disabled = false;
        alert('Could not add to cart. Please try again.');
      }
    });
  }
}
window.setupQuickAddDrawer = setupQuickAddDrawer;

// Re-init on Shopify section load in Theme Customizer
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('#hero-slides-container') || e.target.classList.contains('hero-banner-section')) {
    if (window.setupHeroSlider) window.setupHeroSlider();
  }
});


// Dynamic Cart Actions: Stepper (+/-), Remove Item, Clear Cart
function setupCartDrawerActions() {
  document.addEventListener('click', async (e) => {
    // 1. Remove Item
    const removeBtn = e.target.closest('[data-action="cart-remove"]');
    if (removeBtn) {
      e.preventDefault();
      const key = removeBtn.getAttribute('data-item-key');
      if (key) await updateCartItem(key, 0);
      return;
    }

    // 2. Decrease Quantity
    const minusBtn = e.target.closest('[data-action="cart-qty-minus"]');
    if (minusBtn) {
      e.preventDefault();
      const key = minusBtn.getAttribute('data-item-key');
      const currentQty = parseInt(minusBtn.getAttribute('data-current-qty') || '1', 10);
      const newQty = Math.max(0, currentQty - 1);
      if (key) await updateCartItem(key, newQty);
      return;
    }

    // 3. Increase Quantity
    const plusBtn = e.target.closest('[data-action="cart-qty-plus"]');
    if (plusBtn) {
      e.preventDefault();
      const key = plusBtn.getAttribute('data-item-key');
      const currentQty = parseInt(plusBtn.getAttribute('data-current-qty') || '1', 10);
      const newQty = currentQty + 1;
      if (key) await updateCartItem(key, newQty);
      return;
    }

    // 4. Clear Cart
    const clearBtn = e.target.closest('[data-action="clear-cart"]');
    if (clearBtn) {
      e.preventDefault();
      try {
        clearBtn.textContent = 'Clearing...';
        await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        const cartResp = await fetch('/cart.js');
        const cartData = await cartResp.json();
        renderCartUI(cartData);
      } catch (err) {
        console.error('Error clearing cart:', err);
      }
      return;
    }
  });
}

async function updateCartItem(key, quantity) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: Number(quantity) })
    });
    if (!res.ok) throw new Error('Cart change failed');
    const cartData = await res.json();
    renderCartUI(cartData);
  } catch (err) {
    console.error('Error updating cart item:', err);
  }
}

function renderCartUI(cartData) {
  // 1. Update count badges & product card banners
  document.querySelectorAll('.cart-count-badge').forEach(b => {
    b.textContent = cartData.item_count;
  });
  updateCartAddedBanners(cartData);

  // 2. Clear All button visibility
  const clearBtn = document.getElementById('cart-clear-all-btn');
  if (clearBtn) {
    clearBtn.textContent = 'Clear All';
    if (cartData.item_count > 0) {
      clearBtn.classList.remove('hidden');
    } else {
      clearBtn.classList.add('hidden');
    }
  }

  // 3. Update Subtotal and Total
  const formatMoney = (cents) => '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  const subtotalEl = document.getElementById('cart-subtotal');
  const totalEl = document.getElementById('cart-total');
  if (subtotalEl) subtotalEl.textContent = formatMoney(cartData.total_price);
  if (totalEl) totalEl.textContent = formatMoney(cartData.total_price);

  // 4. Free shipping progress
  const threshold = 99900;
  const pct = Math.min(100, Math.round((cartData.total_price / threshold) * 100));
  const progressEl = document.getElementById('free-shipping-progress');
  const shippingTextEl = document.getElementById('free-shipping-text');
  if (progressEl) progressEl.style.width = pct + '%';
  if (shippingTextEl) {
    if (cartData.total_price >= threshold) {
      shippingTextEl.innerHTML = '<span class="text-emerald-400 font-bold">BAM! You unlocked FREE SHIPPING across India!</span>';
    } else {
      const diff = threshold - cartData.total_price;
      shippingTextEl.innerHTML = '<span>Add <strong class="text-white">' + formatMoney(diff) + '</strong> more for <strong class="text-dwoft-red">FREE SHIPPING</strong></span>';
    }
  }

  // 5. Update footer disabled state
  const footerEl = document.getElementById('cart-footer');
  if (footerEl) {
    if (cartData.item_count === 0) {
      footerEl.classList.add('opacity-50', 'pointer-events-none');
    } else {
      footerEl.classList.remove('opacity-50', 'pointer-events-none');
    }
  }

  // 6. Update Items Container
  const container = document.getElementById('cart-items-container');
  if (!container) return;

  if (cartData.item_count === 0) {
    container.innerHTML = `
      <div class="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div class="w-16 h-16 rounded-2xl bg-[#161720] border border-zinc-800 flex items-center justify-center mb-4 text-dwoft-red shadow-[0_0_20px_rgba(255,8,52,0.2)]">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>
        </div>
        <h4 class="font-display font-extrabold text-lg text-white">Your bag is empty</h4>
        <p class="text-xs text-zinc-400 mt-1 max-w-xs">Looks like you haven't copped any fresh drip yet.</p>
        <a href="/products" class="btn-primary mt-6 !py-3 !px-6 text-xs uppercase tracking-wider font-extrabold">Shop New Drops</a>
      </div>
    `;
    return;
  }

  container.innerHTML = cartData.items.map(item => `
    <div class="flex gap-3.5 p-3.5 bg-[#0D0E12] border border-zinc-800 rounded-xl relative hover:border-zinc-700 transition-colors group" data-cart-item-key="${item.key}">
      ${item.image ? `<img src="${item.image}" alt="${item.title}" width="72" height="90" class="w-18 h-22 object-cover rounded-lg border border-zinc-800 flex-shrink-0">` : ''}
      <div class="flex-1 flex flex-col justify-between min-w-0">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <h4 class="font-bold text-xs sm:text-sm text-white truncate">${item.product_title || item.title}</h4>
            ${item.variant_title ? `<p class="text-[11px] text-zinc-400 mt-0.5 truncate">${item.variant_title}</p>` : ''}
            <p class="text-xs font-mono font-bold text-dwoft-red mt-1">${formatMoney(item.price)}</p>
          </div>
          <button
            type="button"
            data-action="cart-remove"
            data-item-key="${item.key}"
            class="p-1.5 text-zinc-400 hover:text-dwoft-red hover:bg-[#1C1C21] rounded-lg transition-colors flex-shrink-0 cursor-pointer"
            title="Remove from bag"
            aria-label="Remove item"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          </button>
        </div>
        <div class="flex items-center justify-between mt-2.5 pt-2 border-t border-zinc-800/60">
          <div class="inline-flex items-center rounded-lg border border-zinc-700 bg-[#161720] overflow-hidden">
            <button
              type="button"
              data-action="cart-qty-minus"
              data-item-key="${item.key}"
              data-current-qty="${item.quantity}"
              class="px-2.5 py-1 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors font-mono font-bold text-xs cursor-pointer"
              aria-label="Decrease quantity"
            >−</button>
            <span class="px-2.5 py-1 font-mono font-bold text-xs text-white select-none">${item.quantity}</span>
            <button
              type="button"
              data-action="cart-qty-plus"
              data-item-key="${item.key}"
              data-current-qty="${item.quantity}"
              class="px-2.5 py-1 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors font-mono font-bold text-xs cursor-pointer"
              aria-label="Increase quantity"
            >+</button>
          </div>
          <span class="font-display font-extrabold text-sm text-white">${formatMoney(item.line_price)}</span>
        </div>
      </div>
    </div>
  `).join('');
}

// Live Predictive Search Modal (Fit & Collection Pills, Synonyms, Smart Fallback)
function setupSearchModal() {
  const triggers = document.querySelectorAll('[data-action="open-search"]');
  const modal = document.getElementById('search-modal');
  const input = document.getElementById('search-modal-input');
  const defaultState = document.getElementById('search-default-state');
  const resultsContainer = document.getElementById('search-live-results');
  const closeBtn = document.getElementById('close-search-modal');

  if (!modal) return;

  const openModal = () => {
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    if (input) {
      input.value = '';
      if (defaultState) defaultState.classList.remove('hidden');
      if (resultsContainer) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
      }
      setTimeout(() => input.focus(), 50);
    }
  };

  const closeModal = () => {
    modal.classList.add('hidden');
    document.body.style.overflow = '';
  };

  triggers.forEach(t => {
    t.addEventListener('click', (e) => {
      e.preventDefault();
      openModal();
    });
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Global Keyboard Shortcuts (/ to open, Escape to close)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.classList.contains('hidden')) {
      closeModal();
    } else if (e.key === '/' && modal.classList.contains('hidden')) {
      const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
      if (activeTag !== 'input' && activeTag !== 'textarea') {
        e.preventDefault();
        openModal();
      }
    }
  });

  // Synonym / Normalization Helper for Curated Collections
  const normalizeQuery = (rawQuery) => {
    const q = rawQuery.toLowerCase().trim();
    if (q === 'baggy' || q === 'loose' || q === 'box' || q === 'boxy') return 'oversized';
    if (q === 'vneck' || q === 'v neck' || q === 'v-cut' || q === 'v cut') return 'v-neck';
    if (q === 'basic' || q === 'casual' || q === 'plain') return 'tee';
    if (q === 'heavy' || q === 'loopback' || q === 'fleece') return 'terry';
    return q;
  };

  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  // Perform Search Logic
  const performSearch = async (rawQuery) => {
    const q = rawQuery.trim();
    if (!q || q.length < 2) {
      if (defaultState) defaultState.classList.remove('hidden');
      if (resultsContainer) {
        resultsContainer.classList.add('hidden');
        resultsContainer.innerHTML = '';
      }
      return;
    }

    if (defaultState) defaultState.classList.add('hidden');
    if (resultsContainer) {
      resultsContainer.classList.remove('hidden');
      resultsContainer.innerHTML = `
        <div class="py-8 text-center text-xs font-mono flex items-center justify-center gap-2" style="color: #A1A1AA !important;">
          <svg class="animate-spin h-4 w-4" style="color: #FF6A00 !important;" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
          SEARCHING VAULT DROPS...
        </div>
      `;
    }

    const searchQuery = normalizeQuery(q);

    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[limit]=8`);
      const data = await res.json();
      const products = data.resources?.results?.products || [];

      if (!resultsContainer) return;

      if (products.length === 0) {
        const fallbackEl = document.getElementById('search-fallback-recommendations');
        const fallbackHtml = fallbackEl ? fallbackEl.innerHTML : '';
        resultsContainer.innerHTML = `
          <div class="py-2 space-y-4">
            <div class="p-3.5 rounded-xl bg-[#202024] border border-[#27272A] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" style="background-color: #202024 !important; border: 1px solid #27272A !important;">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full shrink-0" style="background-color: #FF6A00 !important;"></span>
                <span class="text-xs font-mono font-bold text-white uppercase tracking-wider">
                  NO EXACT MATCH FOR "<span style="color: #FF6A00 !important;">${escapeHtml(q)}</span>"
                </span>
              </div>
              <span class="search-sublabel" style="color: #A1A1AA !important;">SIGNATURE DROPS</span>
            </div>

            <div>
              <span class="search-label block mb-2 px-1" style="color: #A1A1AA !important;">
                EXPLORE BY SIGNATURE FIT
              </span>
              <div class="flex flex-wrap gap-2">
                <button type="button" data-search-pill="Terry Oversized" class="search-pill">Terry Oversized</button>
                <button type="button" data-search-pill="Classic Oversized" class="search-pill">Classic Oversized</button>
                <button type="button" data-search-pill="Normal Tees" class="search-pill">Normal Tees</button>
                <button type="button" data-search-pill="V-Neck" class="search-pill">V-Neck</button>
              </div>
            </div>

            <div class="pt-2">
              <span class="search-label block mb-2 px-1" style="color: #A1A1AA !important;">
                POPULAR RECOMMENDATIONS
              </span>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                ${fallbackHtml}
              </div>
            </div>

            <div class="pt-2 text-center">
              <a href="/products" class="btn-primary !py-2.5 !px-6 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2" style="background-color: #FF6A00 !important; color: #FFFFFF !important; border-radius: 0.75rem;">
                BROWSE FULL CATALOG →
              </a>
            </div>
          </div>
        `;
        bindPills();
      } else {
        resultsContainer.innerHTML = `
          <div class="space-y-3">
            <div class="flex items-center justify-between pb-1 border-b border-[#27272A]" style="border-bottom: 1px solid #27272A !important;">
              <span class="search-label" style="color: #A1A1AA !important;">
                FOUND ${products.length} RESULT${products.length > 1 ? 'S' : ''}
              </span>
              <span class="text-[10px] font-mono font-bold" style="color: #FF6A00 !important;">LIVE DROPS</span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[440px] overflow-y-auto pr-1">
              ${products.map(p => `
                <a
                  href="${p.url}"
                  class="search-product-card group"
                >
                  ${p.image ? `
                    <img
                      src="${p.image}"
                      alt="${escapeHtml(p.title)}"
                      class="w-14 h-16 object-cover rounded-lg border border-[#2E2E33] group-hover:scale-105 transition-transform shrink-0"
                    >
                  ` : `
                    <div class="w-14 h-16 rounded-lg bg-[#27272A] flex items-center justify-center font-display text-xs text-[#FF6A00] font-black shrink-0">DWOFT</div>
                  `}
                  <div class="min-w-0 flex-1">
                    <span class="search-product-type">
                      ${p.type ? escapeHtml(p.type) : 'STREET DROP'}
                    </span>
                    <h4 class="search-product-title">
                      ${escapeHtml(p.title)}
                    </h4>
                    <p class="search-product-price">
                      ${p.price}
                    </p>
                  </div>
                  <span class="search-view-btn">VIEW</span>
                </a>
              `).join('')}
            </div>
            <div class="pt-2 text-center border-t border-[#27272A]" style="border-top: 1px solid #27272A !important;">
              <a href="/search?q=${encodeURIComponent(q)}" class="search-footer-link text-xs font-mono uppercase font-bold" style="color: #FF6A00 !important;">
                View all results for "${escapeHtml(q)}" →
              </a>
            </div>
          </div>
        `;
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  // Wire up quick category pills
  const bindPills = () => {
    document.querySelectorAll('.search-pill[data-search-pill]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const pillQuery = btn.getAttribute('data-search-pill');
        if (input && pillQuery) {
          input.value = pillQuery;
          performSearch(pillQuery);
        }
      });
    });
  };
  bindPills();

  // Debounced input handler
  let debounceTimer;
  if (input) {
    input.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        performSearch(e.target.value);
      }, 250);
    });
  }
}

// LocalStorage Wishlist & Dedicated Wishlist Page System
function setupWishlistModal() {
  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem('dwoft_wishlist') || '[]');
    } catch { return []; }
  };

  const updateBadges = () => {
    const list = getWishlist();
    document.querySelectorAll('.wishlist-count-badge').forEach(b => {
      b.textContent = list.length;
      if (list.length > 0) {
        b.classList.remove('hidden');
      } else {
        b.classList.add('hidden');
      }
    });

    const pageCount = document.getElementById('wishlist-page-count');
    if (pageCount) {
      pageCount.textContent = '(' + list.length + ')';
    }

    const clearBtn = document.getElementById('wishlist-clear-all');
    if (clearBtn) {
      if (list.length > 0) {
        clearBtn.classList.remove('hidden');
      } else {
        clearBtn.classList.add('hidden');
      }
    }

    // Sync all heart buttons on page
    document.querySelectorAll('[data-wishlist-id]').forEach(btn => {
      const pid = btn.getAttribute('data-wishlist-id');
      if (list.some(i => String(i.id) === String(pid))) {
        btn.classList.add('text-dwoft-red', 'is-active');
        btn.classList.remove('text-zinc-300');
      } else {
        btn.classList.remove('text-dwoft-red', 'is-active');
        btn.classList.add('text-zinc-300');
      }
    });
  };

  // Render on dedicated /pages/wishlist or /search?view=wishlist page
  const renderWishlistPage = () => {
    const container = document.getElementById('wishlist-grid-container');
    if (!container) return;
    const list = getWishlist();

    if (list.length === 0) {
      container.innerHTML = `
        <div class="text-center py-12 sm:py-16 px-4 sm:px-8 bg-white border border-[#E8E3D9] rounded-3xl max-w-lg mx-auto shadow-sm my-4">
          <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#FFF5EB] border border-[#FF6A00]/30 flex items-center justify-center mx-auto mb-5 text-[#FF6A00]">
            <svg class="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
          </div>
          <span style="display:inline-flex;align-items:center;font-family:monospace;font-size:10px;font-weight:800;background:#FFF5EB;color:#FF6A00;border:1px solid #FFD4BA;border-radius:4px;padding:2px 8px;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:0.5rem;">0 SAVED PIECES</span>
          <h2 class="font-display font-extrabold text-2xl sm:text-3xl text-[#151515] mb-2 tracking-tight">YOUR WISHLIST IS EMPTY</h2>
          <p class="text-xs sm:text-sm text-[#6E6B65] mb-8 max-w-xs mx-auto leading-relaxed">
            Hit the heart icon on any drop you like to save it for later.
          </p>
          <a href="/products" class="btn-primary !py-3.5 !px-8 text-xs sm:text-sm uppercase tracking-wider font-extrabold inline-flex items-center gap-2 group active:scale-95">
            <span>EXPLORE ALL DROPS</span>
            <svg class="w-4 h-4 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
            </svg>
          </a>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-6">
        ${list.map(item => `
          <div class="dwoft-product-card group overflow-hidden flex flex-col justify-between rounded-2xl border border-[#E8E3D9] bg-white transition-all duration-300 relative" style="box-shadow:0 2px 10px rgba(0,0,0,0.05);" onmouseover="this.style.borderColor='#FF6A00';this.style.transform='translateY(-4px)';this.style.boxShadow='0 12px 28px rgba(255,106,0,0.15)';" onmouseout="this.style.borderColor='#E8E3D9';this.style.transform='translateY(0)';this.style.boxShadow='0 2px 10px rgba(0,0,0,0.05)';">
            <div class="relative overflow-hidden aspect-[3/4] border-b border-[#EFE8DE]" style="background:linear-gradient(160deg,#FFF5ED 0%,#FFE9D6 100%);">
              <a href="${item.url}" class="block w-full h-full p-2.5">
                ${item.image ? `<img src="${item.image}" alt="${item.title}" class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" loading="lazy">` : `<div class="w-full h-full flex items-center justify-center font-bold text-sm text-[#FF6A00] font-mono">DWOFT</div>`}
              </a>

              <!-- Cart Added Indicator Banner (Top-Middle) -->
              <div 
                class="cart-added-banner absolute top-2.5 sm:top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none transition-all duration-300 hidden opacity-0 scale-90"
                data-cart-banner-product-id="${item.id}"
                data-cart-banner-variant-ids="${item.variantId || ''}"
              >
                <div style="display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:999px;background:#FF6A00;color:#FFFFFF;font-family:'Space Grotesk',sans-serif;font-weight:800;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;white-space:nowrap;box-shadow:0 3px 10px rgba(255,106,0,0.35);">
                  <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                  ADDED!
                </div>
              </div>

              <!-- Remove From Wishlist -->
              <button 
                type="button" 
                data-remove-wishlist="${item.id}" 
                class="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-white/90 border border-[#E8E3D9] text-[#151515] hover:border-[#FF6A00] hover:text-[#FF6A00] active:scale-90 flex items-center justify-center transition-all cursor-pointer shadow-sm" 
                title="Remove from wishlist"
                aria-label="Remove from wishlist"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <!-- Quick Add or View Button -->
              <div class="absolute bottom-2 inset-x-2 sm:bottom-3 sm:inset-x-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                ${item.variantId ? `
                  <button 
                    type="button" 
                    data-quick-add-variant="${item.variantId}"
                    class="w-full min-h-[36px] py-2 px-3 bg-[#151515] hover:bg-[#FF6A00] text-white font-mono font-black text-[10px] sm:text-xs uppercase tracking-wider rounded-xl flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-[#FF6A00] shadow-md"
                  >
                    <span class="text-sm font-black leading-none">+</span>
                    <span>ADD TO BAG</span>
                  </button>
                ` : `
                  <a 
                    href="${item.url}" 
                    class="w-full min-h-[36px] py-2 px-3 bg-[#151515] hover:bg-[#FF6A00] text-white font-mono font-black text-[10px] sm:text-xs uppercase tracking-wider rounded-xl flex items-center justify-center gap-1.5 transition-all border border-[#FF6A00] shadow-md"
                  >
                    <span>VIEW PIECE &rarr;</span>
                  </a>
                `}
              </div>
            </div>

            <div class="p-3 sm:p-4 flex-1 flex flex-col justify-between bg-white">
              <div>
                <span class="text-[9px] sm:text-[10px] font-mono font-black uppercase tracking-widest text-[#8C877D] block truncate">SAVED DROP</span>
                <h3 class="font-sans font-bold text-xs sm:text-sm text-[#151515] line-clamp-1 mt-1" style="overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:1;">
                  <a href="${item.url}" style="color:inherit;text-decoration:none;" onmouseover="this.style.color='#FF6A00'" onmouseout="this.style.color='#151515'">${item.title}</a>
                </h3>
              </div>
              <div class="pt-2 sm:pt-2.5 mt-2 sm:mt-2.5 border-t border-[#F0ECE4] flex items-center justify-between">
                <span class="font-mono font-bold text-xs sm:text-sm text-[#151515]">${item.price || ''}</span>
                <a href="${item.url}" class="text-[11px] font-mono text-[#6E6B65] hover:text-[#FF6A00] transition-colors underline">Details</a>
              </div>
            </div>
          </div>
        `).join('')}
      </div>
    `;

    // Bind remove button handlers
    container.querySelectorAll('[data-remove-wishlist]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.getAttribute('data-remove-wishlist');
        const updated = getWishlist().filter(i => String(i.id) !== String(id));
        localStorage.setItem('dwoft_wishlist', JSON.stringify(updated));
        renderWishlistPage();
        renderWishlistModal();
        updateBadges();
      });
    });

    // Bind quick add handlers
    container.querySelectorAll('[data-quick-add-variant]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-quick-add-variant');
        const origText = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = '<svg class="w-3.5 h-3.5 animate-spin inline-block mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> <span>ADDING...</span>';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          if (res.ok) {
            await refreshCartBadges();

            const card = btn.closest('.card-brutal-hover');
            if (card) {
              const banner = card.querySelector('.cart-added-banner');
              if (banner) {
                banner.classList.remove('hidden');
                requestAnimationFrame(() => {
                  banner.classList.remove('opacity-0', 'scale-90');
                  banner.classList.add('inline-flex', 'opacity-100', 'scale-100');
                });
              }
            }

            btn.classList.remove('bg-dwoft-red', 'hover:bg-[#E00028]');
            btn.classList.add('bg-emerald-600', 'hover:bg-emerald-500');
            btn.innerHTML = '<svg class="w-3.5 h-3.5 inline-block mr-1 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg> <span>ADDED TO BAG</span>';
            setTimeout(() => {
              btn.classList.remove('bg-emerald-600', 'hover:bg-emerald-500');
              btn.classList.add('bg-dwoft-red', 'hover:bg-[#E00028]');
              btn.innerHTML = origText;
              btn.disabled = false;
            }, 2500);
          } else {
            btn.innerHTML = 'VIEW PIECE';
            btn.disabled = false;
            const card = btn.closest('[data-wishlist-card]');
            const prodUrl = card ? card.querySelector('a')?.getAttribute('href') : null;
            if (prodUrl) {
              btn.onclick = () => window.location.href = prodUrl;
            }
          }
        } catch {
          btn.innerHTML = origText;
          btn.disabled = false;
        }
      });
    });

    // Check cart contents to display ADDED! banners on wishlisted items
    refreshCartBadges();
  };

  // Render on popup modal (if still rendered anywhere)
  const renderWishlistModal = () => {
    const container = document.getElementById('wishlist-items-container');
    if (!container) return;
    const list = getWishlist();
    if (list.length === 0) {
      container.innerHTML = `
        <div class="py-8 text-center">
          <p class="text-sm font-bold text-white">Your wishlist is empty</p>
          <p class="text-xs text-zinc-400 mt-1">Tap the heart icon on any fit to save it here.</p>
        </div>
      `;
      return;
    }

    container.innerHTML = list.map(item => `
      <div class="flex items-center gap-3 p-2.5 rounded-xl bg-[#0D0E12] border border-zinc-800">
        ${item.image ? `<img src="${item.image}" alt="${item.title}" class="w-14 h-16 object-cover rounded-lg border border-zinc-700 flex-shrink-0">` : ''}
        <div class="min-w-0 flex-1">
          <h4 class="font-bold text-xs text-white truncate">${item.title}</h4>
          <p class="font-mono font-bold text-xs text-dwoft-red mt-0.5">${item.price || ''}</p>
        </div>
        <div class="flex items-center gap-2">
          <a href="${item.url || '/products'}" class="btn-primary !py-1.5 !px-2.5 text-[11px] uppercase font-bold">View</a>
          <button data-remove-wishlist="${item.id}" class="p-1.5 text-zinc-400 hover:text-dwoft-red transition-colors cursor-pointer" title="Remove">✕</button>
        </div>
      </div>
    `).join('');

    container.querySelectorAll('[data-remove-wishlist]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = btn.getAttribute('data-remove-wishlist');
        const updated = getWishlist().filter(i => String(i.id) !== String(id));
        localStorage.setItem('dwoft_wishlist', JSON.stringify(updated));
        renderWishlistModal();
        renderWishlistPage();
        updateBadges();
      });
    });
  };

  // Hook up clear all button on page
  const clearBtn = document.getElementById('wishlist-clear-all');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      if (confirm('Are you sure you want to clear your saved wishlist?')) {
        localStorage.removeItem('dwoft_wishlist');
        renderWishlistPage();
        renderWishlistModal();
        updateBadges();
      }
    });
  }

  // Global delegated click listeners for wishlist
  document.addEventListener('click', (e) => {
    // 1. Heart toggle button
    const heartBtn = e.target.closest('[data-wishlist-id]');
    if (heartBtn) {
      e.preventDefault();
      e.stopPropagation();

      const id = heartBtn.getAttribute('data-wishlist-id');
      
      // Explicit data attributes first for guaranteed accurate URL and title
      let title = heartBtn.getAttribute('data-product-title');
      let url = heartBtn.getAttribute('data-product-url');
      let image = heartBtn.getAttribute('data-product-image');
      let price = heartBtn.getAttribute('data-product-price');
      let variantId = heartBtn.getAttribute('data-variant-id');

      // Fallbacks if data attributes were omitted
      if (!title || !url) {
        const card = heartBtn.closest('.card-brutal-hover') || heartBtn.closest('.card-brutal') || heartBtn.closest('article') || document;
        const titleLink = card.querySelector('h3 a, h2 a, h1 a, h4 a, .font-accent a');
        if (titleLink) {
          title = titleLink.textContent.trim();
          url = titleLink.getAttribute('href');
        } else {
          title = card.querySelector('h1, h2, h3, h4')?.textContent?.trim() || 'Dwoft Streetwear';
          url = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || window.location.pathname;
        }
        image = image || card.querySelector('img')?.src || '';
        price = price || card.querySelector('.text-dwoft-red, .font-mono')?.textContent?.trim() || '';
      }

      // Safeguard against homepage link
      if (!url || url === '/' || url === window.location.origin + '/') {
        url = '/products';
      }

      let list = getWishlist();
      const exists = list.find(i => String(i.id) === String(id));
      if (exists) {
        list = list.filter(i => String(i.id) !== String(id));
      } else {
        list.push({ id, title, image, price, url, variantId });
      }

      localStorage.setItem('dwoft_wishlist', JSON.stringify(list));
      updateBadges();
      renderWishlistPage();
      renderWishlistModal();
      return;
    }
  });

  // Initial badge update & render
  updateBadges();
  renderWishlistPage();
  renderWishlistModal();
}
